// API Configuration
// En dev: URL vacía = mismo origen, Vite hace proxy /api -> backend :8000
// En build: usar VITE_API_URL o vacío (mismo origen si se sirve junto al API)
export const API_BASE_URL = (import.meta.env.VITE_API_URL as string)?.trim() ?? ""
export const API_PREFIX = "/api/v1"

// Middleware API (Altan / celink.mx) — IMEI, estatus línea, cancelar portabilidad
// Default por hostname:
//   - hosts productivos conocidos → api.celink.mx
//   - localhost o cualquier otro  → sandbox.celink.mx
// Override siempre disponible vía VITE_MIDDLEWARE_API_URL.
const PROD_HOSTS = new Set<string>([
  // Patrón red.<marca>.<tld>
  "red.pandamovil.mx",
  "red.celink.mx",
  "red.claricel.net",
  "red.nxum.mx",
  "red.cristocel.com",
  "red.figou.mx",
  "red.beonline.com.mx",
  // Aliases por marca
  "oficina.cristocel.com",
  // Infynit conserva su URL histórica (mayorista.infynit.mx) y su PROPIO
  // middleware (api.infynit.mx), no el de celink — ver detectMiddlewareUrl.
  "mayorista.infynit.mx",
  // iGou (DiDi + iGou) es OMV separado: su OFV vive en oficina.igou.mx y su
  // middleware en api-crm.igou.mx — ver detectMiddlewareUrl.
  "oficina.igou.mx",
  // Baxnet: OMV con instalación OFV aislada; middleware api.baxnet.mx.
  "ofv.baxnet.mx",
])
// Infynit es un OMV separado: sus SIMs/Altán viven en api.infynit.mx.
const INFYNIT_HOSTS = new Set<string>(["mayorista.infynit.mx"])
// iGou/figou es un OMV separado: sus SIMs/Altán viven en api-crm.igou.mx.
const FIGOU_HOSTS = new Set<string>(["oficina.igou.mx", "red.figou.mx"])
// Baxnet es un OMV separado: sus SIMs/Altán viven en api.baxnet.mx.
const BAXNET_HOSTS = new Set<string>(["ofv.baxnet.mx"])
function detectMiddlewareUrl(): string {
  if (typeof window === "undefined") return "https://api.celink.mx"
  const host = window.location.hostname.toLowerCase()
  if (INFYNIT_HOSTS.has(host) || host.endsWith(".infynit.mx")) return "https://api.infynit.mx"
  if (FIGOU_HOSTS.has(host) || host.endsWith(".igou.mx")) return "https://api-crm.igou.mx"
  if (BAXNET_HOSTS.has(host) || host.endsWith(".baxnet.mx")) return "https://api.baxnet.mx"
  if (PROD_HOSTS.has(host) || host.startsWith("red.")) return "https://api.celink.mx"
  return "https://sandbox.celink.mx"
}
export const MIDDLEWARE_API_URL =
  (import.meta.env.VITE_MIDDLEWARE_API_URL as string)?.trim() || detectMiddlewareUrl()

export const API_ENDPOINTS = {
  // Base URL for direct fetch calls
  BASE: API_PREFIX,

  // Dashboard / Inicio (datos agrupados para la pantalla Inicio)
  DASHBOARD: {
    INICIO: `${API_PREFIX}/dashboard/inicio`,
    // Transacciones de la red en un periodo (tarjeta que sigue al filtro del Inicio)
    RESUMEN: `${API_PREFIX}/dashboard/resumen`,
  },

  // Auth
  AUTH: {
    LOGIN: `${API_PREFIX}/auth/login`,
    LOGOUT: `${API_PREFIX}/auth/logout`,
    REFRESH: `${API_PREFIX}/auth/refresh`,
    ME: `${API_PREFIX}/auth/me`,
    FORGOT_PASSWORD: `${API_PREFIX}/auth/forgot-password`,
    RESET_PASSWORD: `${API_PREFIX}/auth/reset-password`,
  },
  
  // Users
  USERS: {
    LIST: `${API_PREFIX}/usuarios`,
    // Hijos directos del titular (sin saldos) para los selectores de vendedor
    // de Inventario/Ventas/Reportes. No depende del permiso de Saldos.
    RED: `${API_PREFIX}/usuarios/red`,
    GET: (id: number) => `${API_PREFIX}/usuarios/${id}`,
    CREATE: `${API_PREFIX}/usuarios`,
    UPDATE: (id: number) => `${API_PREFIX}/usuarios/${id}`,
    ACTIVATE: (id: number) => `${API_PREFIX}/usuarios/${id}/activar`,
    DEACTIVATE: (id: number) => `${API_PREFIX}/usuarios/${id}/desactivar`,
    // Asignar/cambiar el Master (M) de un B (solo A). id_master=null → bajo A.
    MASTER: (id: number) => `${API_PREFIX}/usuarios/${id}/master`,
    ESIM_ACTIVACION: (id: number) => `${API_PREFIX}/usuarios/${id}/esim-activacion`,
    RECARGAS_EXTERNAS_PERMISOS: (id: number) => `${API_PREFIX}/usuarios/${id}/recargas-externas-permisos`,
    CHANGE_PASSWORD: (id: number) => `${API_PREFIX}/usuarios/${id}/cambiar-password`,
    RESET_PASSWORD: (id: number) => `${API_PREFIX}/usuarios/${id}/resetear-password`,
    // SMTP propio por usuario A (audit P7 + sprint F4)
    SMTP_CONFIG: (id: number) => `${API_PREFIX}/usuarios/${id}/smtp-config`,
    SMTP_TEST: (id: number) => `${API_PREFIX}/usuarios/${id}/smtp-test`,
  },

  // SMTP por marca (Admin/A) — sincronizado desde CRM odoo_companies
  ADMIN: {
    BRAND_SMTP: `${API_PREFIX}/admin/brand-smtp`,
    BRAND_SMTP_TEST: `${API_PREFIX}/admin/brand-smtp/test`,
  },

  // Consulta de perfil de línea (solo lectura, distribuidores B). Pega al
  // backend OFV (NO al middleware directo): el backend valida propiedad de red.
  LINE_PROFILE: {
    GET: (id: string, incluirDispositivo = false) =>
      `${API_PREFIX}/lineas/${encodeURIComponent(id)}/perfil` +
      (incluirDispositivo ? "?incluir_dispositivo=true" : ""),
  },

  // Configuración de comisiones por marca (mínimo de activaciones) — A/Admin.
  BRAND_COMMISSION_SETTINGS: `${API_PREFIX}/brand-commission-settings`,
  
  // Wallets
  WALLETS: {
    BALANCES: `${API_PREFIX}/wallets/saldos`,
    BALANCES_USER: (id: number) => `${API_PREFIX}/wallets/saldos/${id}`,
    TRANSFER_BETWEEN: `${API_PREFIX}/wallets/transferir-entre-wallets`,
    TRANSFER_TO_USER: `${API_PREFIX}/wallets/transferir-saldo`,
    // El A carga saldo de SERVICIOS (wallet 4) a su B, sin bono.
    TRANSFER_SERVICIOS: `${API_PREFIX}/wallets/transferir-saldo-servicios`,
    // Corrección: el A saca saldo de servicios a su B, con motivo.
    RETIRO_SERVICIOS: `${API_PREFIX}/wallets/retirar-saldo-servicios`,
    HISTORY: `${API_PREFIX}/wallets/historial`,
    GRUPO: `${API_PREFIX}/wallets/grupo`,
    USUARIOS: `${API_PREFIX}/wallets/usuarios`,
    USERS_LIST: `${API_PREFIX}/wallets/usuarios`,
    TRANSACCIONES: (id: number) => `${API_PREFIX}/wallets/transacciones/${id}`,
    DESCUENTA: `${API_PREFIX}/wallets/descuenta-saldo`,
    ACTUALIZAR: `${API_PREFIX}/wallets/actualizar`,
  },
  
  // SIMs
  SIMS: {
    LIST: `${API_PREFIX}/sims`,
    ASSIGNED: `${API_PREFIX}/sims/asignadas`,
    COUNTS_BY_VENDOR: `${API_PREFIX}/sims/counts-by-vendor`,
    EXPORT: `${API_PREFIX}/sims/export`,
    POOL_ESIM: `${API_PREFIX}/sims/pool-esim`,
    LIBERAR_ESIM: `${API_PREFIX}/sims/liberar-esim`,
    ESIM_QR: `${API_PREFIX}/sims/esim/qr`,
    ESIM_SEND_EMAIL: `${API_PREFIX}/sims/esim/enviar-correo`,
    ESIM_RECUPERAR_QR: `${API_PREFIX}/sims/esim/recuperar-qr`,
    ESIM_POOL_AVAILABILITY: `${API_PREFIX}/sims/esim-pool-availability`,
    GET: (iccid: string) => `${API_PREFIX}/sims/${iccid}`,
    UPLOAD: `${API_PREFIX}/sims/subir-archivo`,
    ASSIGN: `${API_PREFIX}/sims/asignar`,
    ASSIGN_FILE: `${API_PREFIX}/sims/asignar-archivo`,
    REMOVE_ASSIGNMENT: `${API_PREFIX}/sims/remover-asignacion`,
    REMOVE_VENDOR: `${API_PREFIX}/sims/desasignar-vendor`,
    REMOVE_FILE: `${API_PREFIX}/sims/remover-archivo`,
  },
  
  // Commissions
  COMMISSIONS: {
    BASE: `${API_PREFIX}/comisiones`,
    CONFIG: `${API_PREFIX}/comisiones/configuracion`,
    CONFIG_USER: (id: number) => `${API_PREFIX}/comisiones/configuracion/${id}`,
    CONFIG_CHILDREN: `${API_PREFIX}/comisiones/configuracion-hijos`,
    SUMMARY: `${API_PREFIX}/comisiones/resumen`,
    GENERATED: `${API_PREFIX}/comisiones/generadas`,
    REQUEST_PAYMENT: `${API_PREFIX}/comisiones/solicitar-pago`,
    PAYMENT_REQUESTS: `${API_PREFIX}/comisiones/solicitudes`,
    PAY: `${API_PREFIX}/comisiones/pagar`,
    CANCEL: `${API_PREFIX}/comisiones/cancelar-solicitud`,
    BY_BALANCE: `${API_PREFIX}/comisiones/por-saldo`,
    CLIENTS: `${API_PREFIX}/comisiones/clientes`,
    RELEASE_TO_WALLET: `${API_PREFIX}/comisiones/liberar-wallet`,
    HIJO: `${API_PREFIX}/comisiones/hijo`,
    GUARDAR: `${API_PREFIX}/comisiones/guardar`,
    HIJOS: `${API_PREFIX}/comisiones/hijos`,
    DETALLE_HIJOS: `${API_PREFIX}/comisiones/detalle-hijos`,
    COMISIONES_PAGAR: `${API_PREFIX}/comisiones/comisiones-pagar`,
    LIBERAR_A_WALLET: `${API_PREFIX}/comisiones/liberar-a-wallet`,
    PAGAR_SOLICITUD: (id: number) => `${API_PREFIX}/comisiones/pagar-solicitud/${id}`,
    REJECT_REQUEST: (id: number) => `${API_PREFIX}/comisiones/rechazar-solicitud/${id}`,
    BREAKDOWN: `${API_PREFIX}/comisiones/desglose`,
    ESTADO_CUENTA: `${API_PREFIX}/comisiones/estado-cuenta`,
    // Esquema propio del usuario (lo que puede heredar a sus descendientes).
    MI_ESQUEMA: `${API_PREFIX}/comisiones/mi-esquema`,
  },

  // Reportes extendidos (Fase 3)
  REPORTS: {
    WALLET_MOVIMIENTOS: `${API_PREFIX}/reportes/wallet-movimientos`,
    FISCAL_DEPOSITOS: `${API_PREFIX}/reportes/fiscal/depositos`,
    EFICIENCIA: `${API_PREFIX}/reportes/eficiencia-comisiones`,
    SIMS_HISTORICO: `${API_PREFIX}/reportes/sims-historico`,
  },
  
  // Deposits (Compras)
  DEPOSITS: {
    LIST: `${API_PREFIX}/depositos`,
    PENDING: `${API_PREFIX}/depositos/pendientes`,
    GET: (id: number) => `${API_PREFIX}/depositos/${id}`,
    CREATE: `${API_PREFIX}/depositos`,
    SOLICITUD_STP: `${API_PREFIX}/depositos/solicitud-stp`,
    // Reglas del pago SPEI (habilitado + monto mínimo). El mínimo NO se quema en el
    // front: si divergiera del backend, la pantalla prometería un mínimo y el webhook
    // retendría por otro.
    STP_CONFIG: `${API_PREFIX}/depositos/stp/config`,
    STP_CONCILIAR: `${API_PREFIX}/depositos/stp/conciliar`,
    RELEASE: `${API_PREFIX}/depositos/liberar`,
    REJECT: `${API_PREFIX}/depositos/rechazar`,
    APPROVE: `${API_PREFIX}/depositos/liberar`,
    // Tiempo aire de la bolsa del B (C solicita → B aprueba de su bolsa)
    SOLICITUD_B: `${API_PREFIX}/depositos/solicitud-b`,
    SOLICITUDES_B: `${API_PREFIX}/depositos/solicitudes-b`,
    SOLICITUD_B_APROBAR: `${API_PREFIX}/depositos/solicitud-b/aprobar`,
    SOLICITUD_B_RECHAZAR: `${API_PREFIX}/depositos/solicitud-b/rechazar`,
  },

  // Saldo retenido: abonos SPEI por debajo del mínimo (wallet 5)
  STP_RETENCIONES: {
    LIST: `${API_PREFIX}/stp/retenciones`,
    RESOLVER: (id: number) => `${API_PREFIX}/stp/retenciones/${id}/resolver`,
    // El minimo lo fija el A de cada marca; el .env solo es el valor por omision.
    MONTO_MINIMO: `${API_PREFIX}/stp/monto-minimo`,
  },

  // Facturación CFDI — flujo B/C solicita → A/Admin aprueba → middleware-api timbra
  FACTURAS: {
    SOLICITAR: (id: number) => `${API_PREFIX}/facturacion/solicitar/${id}`,
    APROBAR: (id: number) => `${API_PREFIX}/facturacion/${id}/aprobar`,
    RECHAZAR: (id: number) => `${API_PREFIX}/facturacion/${id}/rechazar`,
    POR_DEPOSITO: (id: number) => `${API_PREFIX}/facturacion/deposito/${id}`,
    LISTA: `${API_PREFIX}/facturacion`,
    PDF: (id: number) => `${API_PREFIX}/facturacion/${id}/pdf`,
    XML: (id: number) => `${API_PREFIX}/facturacion/${id}/xml`,
    REINTENTAR: (id: number) => `${API_PREFIX}/facturacion/${id}/reintentar`,
  },
  
  // Sales
  SALES: {
    LIST: `${API_PREFIX}/ventas`,
    SUMMARY: `${API_PREFIX}/ventas/resumen`,
    TRANSACTIONS: `${API_PREFIX}/ventas/transacciones`,
    CREATE: `${API_PREFIX}/ventas/recarga`,
    RECHARGE: `${API_PREFIX}/ventas/recarga`,
    PORTABILITY: `${API_PREFIX}/ventas/portabilidad`,
    PORTABILITIES: `${API_PREFIX}/ventas/portabilidades`,
  },
  
  // Content
  CONTENT: {
    PUBLICITY: `${API_PREFIX}/contenido/publicidad`,
    ADS: `${API_PREFIX}/contenido/publicidad`,
    UPDATE_PUBLICITY: (id: number) => `${API_PREFIX}/contenido/publicidad/${id}`,
    MANUALS: `${API_PREFIX}/contenido/manuales`,
    UPDATE_MANUAL: (id: number) => `${API_PREFIX}/contenido/manuales/${id}`,
    VIDEOS: `${API_PREFIX}/contenido/videos`,
    DELETE_VIDEO: (id: number) => `${API_PREFIX}/contenido/videos/${id}`,
    NOTICES: `${API_PREFIX}/contenido/avisos`,
    DELETE_NOTICE: (id: number) => `${API_PREFIX}/contenido/avisos/${id}`,
    SECTIONS: `${API_PREFIX}/contenido/secciones`,
    UPDATE_SECTION: (id: number) => `${API_PREFIX}/contenido/secciones/${id}`,
    DELETE_SECTION: (id: number) => `${API_PREFIX}/contenido/secciones/${id}`,
    UPDATE_ORDEN: (tipo: string, id: number) =>
      `${API_PREFIX}/contenido/${tipo}/${id}/orden`,
    REGISTER_DOWNLOAD: (tipo: string, id: number) =>
      `${API_PREFIX}/contenido/${tipo}/${id}/download`,
    TAGS: `${API_PREFIX}/contenido/tags`,
    DELETE_TAG: (id: number) => `${API_PREFIX}/contenido/tags/${id}`,
    SET_ITEM_TAGS: (tipo: string, id: number) =>
      `${API_PREFIX}/contenido/${tipo}/${id}/tags`,
    LIST_VERSIONS: (tipo: string, id: number) =>
      `${API_PREFIX}/contenido/${tipo}/${id}/versiones`,
    REPLACE_FILE: (tipo: string, id: number) =>
      `${API_PREFIX}/contenido/${tipo}/${id}/replace`,
    STATS: `${API_PREFIX}/contenido/stats`,
  },
  
  // Bank Accounts
  BANK_ACCOUNTS: {
    LIST: `${API_PREFIX}/cuentas-bancarias`,
    PRIMARY: `${API_PREFIX}/cuentas-bancarias/principal`,
    CREATE: `${API_PREFIX}/cuentas-bancarias`,
    UPDATE: (id: number) => `${API_PREFIX}/cuentas-bancarias/${id}`,
    DELETE: (id: number) => `${API_PREFIX}/cuentas-bancarias/${id}`,
    SET_PRIMARY: (id: number) => `${API_PREFIX}/cuentas-bancarias/${id}/establecer-principal`,
  },
  
  // Fiscal Data
  FISCAL: {
    GET: `${API_PREFIX}/datos-fiscales`,
    SAVE: `${API_PREFIX}/datos-fiscales`,
    UPLOAD_CERTIFICATE: `${API_PREFIX}/datos-fiscales/constancia`,
    VALIDATE_RFC: (rfc: string) => `${API_PREFIX}/datos-fiscales/validar-rfc/${rfc}`,
    REGIMES: `${API_PREFIX}/datos-fiscales/catalogos/regimenes`,
    CFDI_USES: `${API_PREFIX}/datos-fiscales/catalogos/usos-cfdi`,
  },
  
  // Files
  FILES: {
    UPLOAD_PROFILE: `${API_PREFIX}/archivos/imagen-perfil`,
    UPLOAD_DEPOSIT: `${API_PREFIX}/archivos/comprobante-deposito`,
    UPLOAD_SIMS: `${API_PREFIX}/archivos/sims`,
    UPLOAD_CEDULA: `${API_PREFIX}/archivos/cedula`,
    UPLOAD_GENERIC: `${API_PREFIX}/archivos/generico`,
    DELETE: `${API_PREFIX}/archivos`,
    DOWNLOAD: (type: string, name: string) => `${API_PREFIX}/archivos/download/${type}/${name}`,
  },
  
  // External APIs Status y config de aplicación
  EXTERNAL: {
    STATUS: `${API_PREFIX}/external/status`,
    CONFIG: `${API_PREFIX}/external/config`,
    FECHAS_CORTE: `${API_PREFIX}/external/fechas-corte`,
    /** Empresas/capas disponibles (dinámico, para filtrar por capa en consultas) */
    CAPAS: `${API_PREFIX}/external/capas`,
  },

  // Permission Profiles
  PERMISSION_PROFILES: {
    LIST: `${API_PREFIX}/perfiles-permiso`,
    CREATE: `${API_PREFIX}/perfiles-permiso`,
    GET: (id: number) => `${API_PREFIX}/perfiles-permiso/${id}`,
    UPDATE: (id: number) => `${API_PREFIX}/perfiles-permiso/${id}`,
    DELETE: (id: number) => `${API_PREFIX}/perfiles-permiso/${id}`,
    /** Devuelve a la vista un perfil eliminado (el soft delete no borra la fila). */
    REACTIVATE: (id: number) => `${API_PREFIX}/perfiles-permiso/${id}/reactivar`,
    /** Cuentas que tienen el perfil asignado — impacto antes de eliminar. */
    USERS: (id: number) => `${API_PREFIX}/perfiles-permiso/${id}/usuarios`,
    ASSIGN: `${API_PREFIX}/perfiles-permiso/asignar`,
    REMOVE: `${API_PREFIX}/perfiles-permiso/quitar`,
    KEYS: `${API_PREFIX}/perfiles-permiso/claves`,
  },

  // Middleware API (Altan) — endpoints que se llaman directo al middleware-api
  MIDDLEWARE: {
    CHECK_IMEI: (imei: string) => `${MIDDLEWARE_API_URL}/api/page/checkImei?imei=${imei}`,
    CONSULTA_PERFIL: (msisdn: string) => `${MIDDLEWARE_API_URL}/api/page/consultaPerfilUF?msisdn=${msisdn}`,
    REVERSA_IMPORTACION: `${MIDDLEWARE_API_URL}/api/altan/reversaImportacion`,
    GET_MSISDN_BY: (iccid: string) => `${MIDDLEWARE_API_URL}/api/page/getMsisdnBy?iccid=${iccid}`,
    FIND_BY_MSISDN: (msisdn: string) => `${MIDDLEWARE_API_URL}/api/odoo/product/findbymsisdn?msisdn=${msisdn}`,
    RECHARGE_FREE: `${MIDDLEWARE_API_URL}/api/seller/user/rechargefree`,
    ESTADO_PORTABILIDAD: (portid: string) => `${MIDDLEWARE_API_URL}/api/page/estadoportabilidad?portid=${portid}`,
    PORTABILIDAD: `${MIDDLEWARE_API_URL}/api/page/portabilidad`,
  },
} as const
